import { setAccessToken, setCurrentTenant } from '@graal/api';

/** Utilisateur connecté, tel que l'affichent la top bar et le menu utilisateur. */
export type SessionUser = { name: string; email: string; initials: string };

export type Session = {
  tenantId: string;
  realm: string;
  user: SessionUser;
  accessToken: string;
  /** Epoch en secondes, tel que le donne `expires_at` d'oidc-client-ts. */
  expiresAt: number;
};

/** Connexion en cours : posée avant la redirection, relue par /callback (un seul onglet). */
export type PendingSignIn = {
  tenantId: string;
  realm: string;
  returnTo: string;
  /**
   * Fournisseur d'identité Keycloak imposé (`kc_idp_hint`). Seul usage : `admin`, la connexion d'un
   * administrateur de la plateforme à un tenant avec son compte du realm `admin`. Un indice, pas
   * un droit : le serveur vérifie par l'échange de jeton du broker.
   */
  idpHint?: string;
};

const PENDING_KEY = 'graal.auth.pending';
const LAST_TENANT_KEY = 'graal.auth.tenant';

function safeSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null; // navigation privée verrouillée : la session ne survivra pas au rechargement
  }
}

export function setPendingSignIn(pending: PendingSignIn): void {
  safeSession()?.setItem(PENDING_KEY, JSON.stringify(pending));
}

export function takePendingSignIn(): PendingSignIn | null {
  const store = safeSession();
  const raw = store?.getItem(PENDING_KEY);
  if (!raw) return null;
  store?.removeItem(PENDING_KEY);
  try {
    const parsed = JSON.parse(raw) as Partial<PendingSignIn>;
    if (typeof parsed.tenantId !== 'string' || typeof parsed.realm !== 'string') return null;
    const idpHint = typeof parsed.idpHint === 'string' && parsed.idpHint ? parsed.idpHint : undefined;
    return {
      tenantId: parsed.tenantId,
      realm: parsed.realm,
      returnTo: parsed.returnTo ?? '/',
      ...(idpHint ? { idpHint } : {}),
    };
  } catch {
    return null;
  }
}

/** Dernier tenant utilisé : sert à reprendre la session au rechargement, et à pré-remplir /login. */
export function rememberTenant(tenantId: string, realm: string): void {
  safeSession()?.setItem(LAST_TENANT_KEY, JSON.stringify({ tenantId, realm }));
}

export function lastTenant(): { tenantId: string; realm: string } | null {
  try {
    const raw = safeSession()?.getItem(LAST_TENANT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { tenantId?: unknown; realm?: unknown };
    return typeof parsed.tenantId === 'string' && typeof parsed.realm === 'string'
      ? { tenantId: parsed.tenantId, realm: parsed.realm }
      : null;
  } catch {
    return null;
  }
}

export function forgetTenant(): void {
  safeSession()?.removeItem(LAST_TENANT_KEY);
  safeSession()?.removeItem(PENDING_KEY);
}

/** Deux initiales au maximum, sans dépendre de la casse ni des accents. */
export function initialsOf(name: string, fallback: string): string {
  const parts = name
    .trim()
    .split(/[\s.'-]+/u)
    .filter(Boolean);
  const letters = parts.slice(0, 2).map((part) => [...part][0] ?? '');
  const result = letters.join('').toLocaleUpperCase();
  return result || [...fallback.toLocaleUpperCase()].slice(0, 2).join('');
}

/**
 * Publie la session vers @graal/api : le mutator pose ensuite `X-Tenant` et `Authorization`
 * sur chaque requête. Aucun appelant ne touche à ces deux en-têtes.
 */
export function publishSession(session: Session | null): void {
  setCurrentTenant(session?.tenantId ?? '');
  setAccessToken(session?.accessToken ?? null);
}
